/**
 * Pure dungeon-dressing layout (sim yards, no three.js): wall runs with outward normals,
 * doorway openings, wall tiling, wall decor (torches/banners) and deterministic prop scatter.
 */
import type { Polygon, Vec2 } from '@keystone/sim';
import { pointInPolygon } from '../walls';

export interface EdgeRun {
  a: Vec2;
  b: Vec2;
  /** Unit normal pointing out of the walkable polygon. */
  normal: Vec2;
  polyIndex: number;
}

export interface WallLayout {
  walls: EdgeRun[];
  /** Stretches of polygon edge that open into another walkable polygon (doorways). */
  openings: EdgeRun[];
  /** Polygon corners that carry a wall on at least one side (pillar spots). */
  corners: { pos: Vec2; polyIndex: number }[];
}

function signedArea(poly: Polygon): number {
  let s = 0;
  const pts = poly.points;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) s += (pts[j]!.x - pts[i]!.x) * (pts[j]!.y + pts[i]!.y);
  return s / 2;
}

/** Same rule as computeWallSegments (placeholder), but keeps normals, openings and corners. */
export function computeWallLayout(walkable: readonly Polygon[], step = 1, probe = 0.6): WallLayout {
  const walls: EdgeRun[] = [];
  const openings: EdgeRun[] = [];
  const corners: WallLayout['corners'] = [];
  walkable.forEach((poly, polyIndex) => {
    const pts = poly.points;
    if (pts.length < 3) return;
    const ccw = signedArea(poly) >= 0;
    const wallAt: boolean[] = [];
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i]!;
      const b = pts[(i + 1) % pts.length]!;
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      if (len < 1e-6) {
        wallAt.push(false);
        continue;
      }
      const dx = (b.x - a.x) / len;
      const dy = (b.y - a.y) / len;
      const normal = { x: ccw ? dy : -dy, y: ccw ? -dx : dx };
      const n = Math.max(1, Math.round(len / step));
      let runStart = 0;
      let runOpen: boolean | null = null;
      let anyWall = false;
      const flush = (k: number): void => {
        if (runOpen === null) return;
        const t0 = (runStart / n) * len;
        const t1 = (k / n) * len;
        const run: EdgeRun = { a: { x: a.x + dx * t0, y: a.y + dy * t0 }, b: { x: a.x + dx * t1, y: a.y + dy * t1 }, normal, polyIndex };
        (runOpen ? openings : walls).push(run);
        if (!runOpen) anyWall = true;
      };
      for (let k = 0; k < n; k++) {
        const mid = ((k + 0.5) / n) * len;
        const outside = { x: a.x + dx * mid + normal.x * probe, y: a.y + dy * mid + normal.y * probe };
        const open = walkable.some((q) => q !== poly && pointInPolygon(outside, q));
        if (open !== runOpen) {
          flush(k);
          runStart = k;
          runOpen = open;
        }
      }
      flush(n);
      wallAt.push(anyWall);
    }
    for (let i = 0; i < pts.length; i++) {
      const prev = wallAt[(i - 1 + pts.length) % pts.length];
      if (wallAt[i] || prev) corners.push({ pos: { ...pts[i]! }, polyIndex });
    }
  });
  return { walls, openings, corners };
}

export interface WallPiece {
  /** Centre of the piece on the wall's centre line (offset outward by `outset`). */
  center: Vec2;
  normal: Vec2;
  /** Piece length in yards (pieces are stretched to fill the run exactly). */
  length: number;
}

/** Tile a run with pieces of about `pieceLen` yards, pushed `outset` yards outward. */
export function tileRun(run: EdgeRun, pieceLen = 4, outset = 0.5): WallPiece[] {
  const len = Math.hypot(run.b.x - run.a.x, run.b.y - run.a.y);
  if (len < 0.3) return [];
  const n = Math.max(1, Math.round(len / pieceLen));
  const out: WallPiece[] = [];
  for (let k = 0; k < n; k++) {
    const t = (k + 0.5) / n;
    out.push({
      center: { x: run.a.x + (run.b.x - run.a.x) * t + run.normal.x * outset, y: run.a.y + (run.b.y - run.a.y) * t + run.normal.y * outset },
      normal: run.normal,
      length: len / n,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Deterministic RNG
// ---------------------------------------------------------------------------

export function hashString(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

/** mulberry32: small, fast, deterministic. */
export function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---------------------------------------------------------------------------
// Wall decor and props
// ---------------------------------------------------------------------------

function distToSegment(p: Vec2, a: Vec2, b: Vec2): number {
  const vx = b.x - a.x;
  const vy = b.y - a.y;
  const l2 = vx * vx + vy * vy;
  const t = l2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * vx + (p.y - a.y) * vy) / l2)) : 0;
  return Math.hypot(p.x - (a.x + vx * t), p.y - (a.y + vy * t));
}

/** Distance from a point to a polygon (0 inside). */
export function distToPolygon(p: Vec2, poly: Polygon): number {
  if (pointInPolygon(p, poly)) return 0;
  let d = Infinity;
  const pts = poly.points;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) d = Math.min(d, distToSegment(p, pts[j]!, pts[i]!));
  return d;
}

export type WallDecorKind = 'torch' | 'banner';

export interface WallDecor {
  kind: WallDecorKind;
  /** Point on the walkable boundary. */
  pos: Vec2;
  /** Outward wall normal. */
  normal: Vec2;
}

/**
 * Torches and banners along wall runs: one slot every `spacing` yards, alternating kinds,
 * skipping slots near doorways. At most `maxTorches` torches overall (they carry glow FX).
 */
export function placeWallDecor(layout: WallLayout, opts: { spacing?: number; maxTorches?: number; roomCount?: number } = {}): WallDecor[] {
  const spacing = opts.spacing ?? 16;
  const maxTorches = opts.maxTorches ?? 60;
  const out: WallDecor[] = [];
  let torches = 0;
  let slot = 0;
  for (const run of layout.walls) {
    if (opts.roomCount !== undefined && run.polyIndex >= opts.roomCount) continue; // rooms only
    const len = Math.hypot(run.b.x - run.a.x, run.b.y - run.a.y);
    if (len < spacing * 0.6) continue;
    const n = Math.floor(len / spacing);
    for (let k = 0; k < n; k++) {
      const t = (k + 0.5) / n;
      const pos = { x: run.a.x + (run.b.x - run.a.x) * t, y: run.a.y + (run.b.y - run.a.y) * t };
      if (layout.openings.some((o) => distToSegment(pos, o.a, o.b) < 4)) continue;
      const kind: WallDecorKind = slot++ % 3 === 1 ? 'banner' : 'torch';
      if (kind === 'torch') {
        if (torches >= maxTorches) continue;
        torches++;
      }
      out.push({ kind, pos, normal: run.normal });
    }
  }
  return out;
}

export type PropKind = 'crates_stacked' | 'box_large' | 'barrel_large' | 'rubble_half' | 'rubble_large' | 'column' | 'brazier';

export interface PropPlacement {
  kind: PropKind;
  pos: Vec2;
  /** Yaw in sim radians (CCW from +x). */
  rot: number;
  scale: number;
}

/** Weighted prop table: [kind, weight, footprint radius (yd), scale range]. */
export const PROP_TABLE: readonly [PropKind, number, number, [number, number]][] = [
  ['crates_stacked', 3, 1.3, [0.8, 1.0]],
  ['box_large', 3, 1.0, [0.75, 1.0]],
  ['barrel_large', 4, 1.0, [0.65, 0.9]],
  ['rubble_half', 2, 2.2, [0.55, 0.75]],
  ['rubble_large', 1, 3.0, [0.45, 0.6]],
  ['column', 2, 0.6, [0.9, 1.3]],
  ['brazier', 1, 0.9, [1, 1]],
];

export interface ScatterOptions {
  /** Polygons props must stay clear of (corridors / doorways). */
  blocked?: readonly Polygon[];
  blockedClearance?: number;
  /** Points to keep clear (pack spawns, entrance, patrol points). */
  avoid?: readonly Vec2[];
  avoidRadius?: number;
  /** Average yards of wall between props. */
  spacing?: number;
  /** How far in from the wall props sit (yards). Keeps the room centre clear for fights. */
  inset?: [number, number];
}

/**
 * Deterministic props along the inside of a room's walls, seeded from the room id.
 * Props hug the walls, stay out of corridor mouths and away from spawns, and never overlap.
 */
export function scatterProps(room: { id: string; shape: Polygon }, opts: ScatterOptions = {}): PropPlacement[] {
  const rnd = seededRandom(hashString(room.id));
  const blocked = opts.blocked ?? [];
  const clearance = opts.blockedClearance ?? 3.5;
  const avoid = opts.avoid ?? [];
  const avoidR = opts.avoidRadius ?? 5;
  const spacing = opts.spacing ?? 7;
  const [in0, in1] = opts.inset ?? [1.4, 2.4];
  const totalW = PROP_TABLE.reduce((s, r) => s + r[1], 0);
  const pick = (): (typeof PROP_TABLE)[number] => {
    let x = rnd() * totalW;
    for (const row of PROP_TABLE) if ((x -= row[1]) <= 0) return row;
    return PROP_TABLE[0]!;
  };
  const pts = room.shape.points;
  const ccw = signedArea(room.shape) >= 0;
  const out: (PropPlacement & { r: number })[] = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % pts.length]!;
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len < 4) continue;
    const dx = (b.x - a.x) / len;
    const dy = (b.y - a.y) / len;
    // Inward normal (left of the edge for CCW polygons).
    const inx = ccw ? -dy : dy;
    const iny = ccw ? dx : -dx;
    let t = 2 + rnd() * spacing * 0.5;
    while (t < len - 2) {
      const [kind, , footprint, [s0, s1]] = pick();
      const inset = in0 + rnd() * (in1 - in0) + footprint * 0.6;
      const pos = { x: a.x + dx * t + inx * inset, y: a.y + dy * t + iny * inset };
      t += spacing * (0.6 + rnd() * 0.9);
      if (rnd() < 0.3) continue; // leave gaps so walls don't look lined
      if (!pointInPolygon(pos, room.shape)) continue;
      if (blocked.some((p) => distToPolygon(pos, p) < clearance + footprint)) continue;
      if (avoid.some((p) => Math.hypot(p.x - pos.x, p.y - pos.y) < avoidR + footprint)) continue;
      if (out.some((o) => Math.hypot(o.pos.x - pos.x, o.pos.y - pos.y) < o.r + footprint + 0.4)) continue;
      const wallYaw = Math.atan2(iny, inx);
      out.push({ kind, pos, rot: wallYaw + (rnd() - 0.5) * 1.2, scale: s0 + rnd() * (s1 - s0), r: footprint });
    }
  }
  return out.map(({ r: _r, ...p }) => p);
}
